import { useEffect, useMemo, useRef } from "react";
import {
  useCanvasStore,
  isCanvasPosInitialized,
} from "@/components/Canvas/stores/canvasStore";
import { useActiveCanvas } from "@/utils/utils";
import type { Storyboard as StoryboardType } from "@/utils/types";
import {
  START_MIN_ZOOM,
  type SimpleCanvasRef,
} from "../../SimpleCanvas/SimpleCanvas";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";

export const MIN_ZOOM = 0.01;
export const MAX_ZOOM = 5;

// Generated by just testing what looks good
export const TRANSFORM_FOR_DEFAULT_STORY_GENERATION_ITEM = {
  x: 60,
  y: -40,
  zoom: 0.94,
};

export const computeCenteredViewport = (
  items: { x: number; y: number; width: number; height: number }[],
  visibleWidth: number,
  visibleHeight: number,
  bufferFactor: number = 0.7,
  forceZoomValue: number | null = null,
  minZoom: number = MIN_ZOOM,
  maxZoom: number = MAX_ZOOM,
): { x: number; y: number; zoom: number } => {
  if (items.length === 0) {
    return { x: 0, y: 0, zoom: 1 };
  }

  const validItems = items.filter(
    (item) =>
      (item.width || 0) > 0 &&
      (item.height || 0) > 0 &&
      item.x !== undefined &&
      item.y !== undefined,
  );

  const minX = Math.min(...validItems.map((item) => item.x));
  const minY = Math.min(...validItems.map((item) => item.y));
  const maxX = Math.max(...validItems.map((item) => item.x + item.width));
  const maxY = Math.max(...validItems.map((item) => item.y + item.height));

  const viewportWidth = maxX - minX;
  const viewportHeight = maxY - minY;

  if (viewportWidth === 0 || viewportHeight === 0) {
    return { x: 0, y: 0, zoom: 1 };
  }

  const scale =
    Math.min(visibleWidth / viewportWidth, visibleHeight / viewportHeight) *
    bufferFactor;

  const boundedScale = forceZoomValue
    ? forceZoomValue
    : Math.max(minZoom, Math.min(maxZoom, scale));

  // Center the viewport on the items
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  // Since the canvas transform layer is now centered in the container via the centering wrapper,
  // we just need to offset by the negative center position scaled by the zoom
  const x = -centerX * boundedScale;
  const y = -centerY * boundedScale;

  return { x, y, zoom: boundedScale };
};

export const calculateViewportVisibility = (
  items: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    hidden?: boolean;
  }[],
  viewportWidth: number,
  viewportHeight: number,
  transform: { x: number; y: number; zoom: number },
): { visible: Set<string>; invisible: Set<string> } => {
  // Calculate viewport bounds in canvas coordinates
  // The viewport center is at (0,0) in screen space, which maps to canvas coordinates
  // based on the current transform
  const viewportLeft = (-viewportWidth / 2 - transform.x) / transform.zoom;
  const viewportTop = (-viewportHeight / 2 - transform.y) / transform.zoom;
  const viewportRight = (viewportWidth / 2 - transform.x) / transform.zoom;
  const viewportBottom = (viewportHeight / 2 - transform.y) / transform.zoom;

  const visibleIds = new Set<string>();
  const invisibleIds = new Set<string>();

  items.forEach((item) => {
    if (item.hidden) {
      invisibleIds.add(item.id);
      return;
    }

    const itemRight = item.x + item.width;
    const itemBottom = item.y + item.height;

    // Check if item intersects with viewport
    const isVisible = !(
      item.x > viewportRight ||
      itemRight < viewportLeft ||
      item.y > viewportBottom ||
      itemBottom < viewportTop
    );

    if (isVisible) {
      visibleIds.add(item.id);
    } else {
      invisibleIds.add(item.id);
    }
  });

  return { visible: visibleIds, invisible: invisibleIds };
};

export const useCanvasViewportControl = ({
  simpleCanvasRef,
  storyboardsToRender,
}: {
  simpleCanvasRef: React.RefObject<SimpleCanvasRef>;
  storyboardsToRender: (StoryboardType & { hidden?: boolean })[];
}) => {
  const canvasData = useActiveCanvas();
  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey,
  );
  const canvasId = canvasData?.id;
  const isComponentSubCanvas = Boolean(canvasComponentKey);
  // Center view only when canvas actually changes, not on every storyboard update
  const prevCanvasIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      canvasId &&
      simpleCanvasRef.current &&
      canvasId !== prevCanvasIdRef.current
    ) {
      const canvasPos =
        useCanvasStore.getState().canvasIdToPosRealtime[canvasId];
      if (isCanvasPosInitialized(canvasPos)) {
        simpleCanvasRef.current?.setCanvasTransform({
          x: canvasPos.transformX,
          y: canvasPos.transformY,
          zoom: canvasPos.zoomPerc,
        });
      } else {
        const visibleStoryboards = storyboardsToRender.filter(
          (item) => !item.hidden,
        );
        if (visibleStoryboards.length > 0) {
          const bufferFactor = isComponentSubCanvas ? 0.5 : 0.7;
          simpleCanvasRef.current?.centerViewOnItems(
            visibleStoryboards.map((sb) => sb.id),
            bufferFactor,
            false,
            false,
            START_MIN_ZOOM,
          );
        } else if (isComponentSubCanvas) {
          simpleCanvasRef.current?.setCanvasTransform(
            TRANSFORM_FOR_DEFAULT_STORY_GENERATION_ITEM,
          );
        }
      }
      prevCanvasIdRef.current = canvasId;
    }
  }, [canvasId, storyboardsToRender, isComponentSubCanvas]);

  const canvasPosInitialized = useCanvasStore((state) =>
    canvasId ? isCanvasPosInitialized(state.canvasIdToPos[canvasId]) : false,
  );

  useEffect(() => {
    if (canvasPosInitialized) {
      return;
    }

    const visibleStoryboards = storyboardsToRender.filter(
      (item) => !item.hidden,
    );
    if (visibleStoryboards.length > 0) {
      const bufferFactor = isComponentSubCanvas ? 0.5 : 0.7;
      simpleCanvasRef.current?.centerViewOnItems(
        visibleStoryboards.map((sb) => sb.id),
        bufferFactor,
        false,
        false,
        START_MIN_ZOOM,
      );
    }
  }, [storyboardsToRender.length, canvasPosInitialized]);

  const storyboardToScrollTo = useMemo(() => {
    return storyboardsToRender.find(
      (storyboard) => storyboard.scrollToStoryboard,
    );
  }, [storyboardsToRender]);

  /**
   * If a new storyboard is created that needs to be scrolled to, start a process to scroll to that storyboard
   */
  useEffect(() => {
    if (!storyboardToScrollTo || !canvasId || !canvasData) {
      return;
    }

    const canvasPos = useCanvasStore.getState().canvasIdToPosRealtime[canvasId];

    if (!isCanvasPosInitialized(canvasPos)) {
      return;
    }

    const removeTrigger = () => {
      updateCanvasStoryboards(canvasData.id, [
        {
          changeType: "update",
          storyboardId: storyboardToScrollTo.id,
          pageId: storyboardToScrollTo.pageId || "",
          storyboardUpdateDict: {
            scrollToStoryboard: false,
          },
        },
      ]);
    };

    removeTrigger();

    simpleCanvasRef.current?.centerViewOnItems(
      [storyboardToScrollTo.id],
      0.7,
      true,
      true,
    );
  }, [storyboardToScrollTo, canvasId, canvasPosInitialized]);

  const storyboardToJumpTo = useMemo(() => {
    return storyboardsToRender.find(
      (storyboard) => storyboard.jumpToStoryboard,
    );
  }, [storyboardsToRender]);

  useEffect(() => {
    if (!storyboardToJumpTo || !canvasId || !canvasData) {
      return;
    }

    const canvasPos = useCanvasStore.getState().canvasIdToPosRealtime[canvasId];

    if (!isCanvasPosInitialized(canvasPos)) {
      return;
    }

    const removeTrigger = () => {
      updateCanvasStoryboards(canvasData.id, [
        {
          changeType: "update",
          storyboardId: storyboardToJumpTo.id,
          pageId: storyboardToJumpTo.pageId || "",
          storyboardUpdateDict: {
            jumpToStoryboard: false,
          },
        },
      ]);
    };

    removeTrigger();

    simpleCanvasRef.current?.centerViewOnItems(
      [storyboardToJumpTo.id],
      0.7,
      false, // animate = false for instant jump
      true,
    );
  }, [storyboardToJumpTo, canvasId, canvasPosInitialized]);
};
